import { useEffect, useRef } from 'react';
import socketService from '../api/socket';
import { authStore } from '../stores/auth.store';
import { getStoredToken } from '../utils/authSession';

/**
 * Manages the socket.io connection lifecycle inside React.
 *  - Reads the token at render/effect time — no module-level side effects.
 *  - Connects when a token is present, disconnects on logout / unmount.
 *  - Joins the user's own room once the user object is available.
 */
export const useSocketSetup = () => {
  const hasConnectedRef = useRef(false);

  // getStoredToken checks all role-scoped keys (admin_token, assistant_token, user_token)
  const token = getStoredToken();
  const userId = authStore.user?.id || authStore.user?._id;

  useEffect(() => {
    if (!token) {
      // No token — make sure we are disconnected
      socketService.disconnect();
      hasConnectedRef.current = false;
      return;
    }

    if (!hasConnectedRef.current) {
      socketService.connect();
      hasConnectedRef.current = true;
    }

    // Once user is available, emit the join room event
    if (userId && socketService.socket?.connected) {
      socketService.socket.emit('join', userId);
    }

    return () => {
      // Disconnect when AuthProvider unmounts (i.e. user navigates away from
      // all protected routes or logs out)
      socketService.disconnect();
      hasConnectedRef.current = false;
    };
  }, [token, userId]);

  return { socketService };
};
